// Unified Container Tracking Component - Combines SeaRates Map + FCL Features
import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ReminderButton } from "./ReminderComponents";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Search,
  Loader2,
  Container,
  Ship,
  MapPin,
  Clock,
  Calendar,
  Anchor,
  Navigation,
  Box,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle,
  Radio,
  Globe,
  Truck,
  ArrowRight,
  Info,
  LocateFixed,
  Waves,
  Train,
  Tag,
  Mail,
  Image,
  Camera,
  Eye,
  Plus,
  Bell,
  FolderOpen,
  ExternalLink,
} from "lucide-react";


// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom vessel icon
const vesselIcon = new L.DivIcon({
  className: "vessel-marker",
  html: `<div class="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full border-2 border-white shadow-lg">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>
      <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/>
      <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/>
      <path d="M12 10v4"/>
      <path d="M12 2v3"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

// Custom port icon
const portIcon = new L.DivIcon({
  className: "port-marker",
  html: `<div class="flex items-center justify-center w-6 h-6 bg-emerald-600 rounded-full border-2 border-white shadow-md">
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="5" r="3"/>
      <line x1="12" x2="12" y1="22" y2="8"/>
      <path d="M5 12H2a10 10 0 0 0 20 0h-3"/>
    </svg>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Map bounds fitter component
function MapBoundsFitter({ route, vesselPosition }) {
  const map = useMap();
  
  useEffect(() => {
    const bounds = [];
    if (route && route.length > 0) {
      route.forEach(segment => {
        if (segment.path) {
          segment.path.forEach(point => {
            if (Array.isArray(point) && point.length >= 2 && isFinite(point[0]) && isFinite(point[1])) {
              bounds.push([point[0], point[1]]);
            }
          });
        }
      });
    }
    if (vesselPosition && vesselPosition.length >= 2 && isFinite(vesselPosition[0]) && isFinite(vesselPosition[1])) {
      bounds.push(vesselPosition);
    }
    if (bounds.length > 0) {
      try {
        const leafletBounds = L.latLngBounds(bounds);
        map.fitBounds(leafletBounds, { padding: [50, 50], maxZoom: 6 });
      } catch (e) {
        console.warn("Failed to fit map bounds:", e);
      }
    }
  }, [route, vesselPosition, map]);
  
  return null;
}

// Tracking type options
const TRACKING_TYPES = [
  { value: "CT", label: "Container Number", icon: Container },
  { value: "BL", label: "Bill of Lading", icon: Box },
  { value: "BK", label: "Booking Number", icon: Calendar },
];

// Shipping carriers
const CARRIERS = [
  { code: "", name: "Auto-detect Carrier" },
  { code: "MAERSK", name: "Maersk" },
  { code: "MSC", name: "MSC" },
  { code: "CMA_CGM", name: "CMA CGM" },
  { code: "HAPAG_LLOYD", name: "Hapag-Lloyd" },
  { code: "COSCO", name: "COSCO" },
  { code: "EVERGREEN", name: "Evergreen" },
  { code: "ONE", name: "ONE" },
  { code: "YANG_MING", name: "Yang Ming" },
  { code: "ZIM", name: "ZIM" },
  { code: "OOCL", name: "OOCL" },
];

// Common destination ports
const COMMON_PORTS = [
  { code: "KOPER", label: "Koper, Slovenia" },
  { code: "ROTTERDAM", label: "Rotterdam, NL" },
  { code: "HAMBURG", label: "Hamburg, DE" },
  { code: "ANTWERP", label: "Antwerp, BE" },
  { code: "TRIESTE", label: "Trieste, IT" },
  { code: "PIRAEUS", label: "Piraeus, GR" },
];

export default function UnifiedContainerTracker({ getAuthHeaders, isEuropacificUser, user }) {
  // Search parameters
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingType, setTrackingType] = useState("CT");
  const [carrier, setCarrier] = useState("");
  const [destinationPort, setDestinationPort] = useState("KOPER");
  const [transportType, setTransportType] = useState("port_to_port");
  
  // Additional fields (staff features)
  const [pfNumber, setPfNumber] = useState("");
  const [notificationEmails, setNotificationEmails] = useState("");
  const [metransRef, setMetransRef] = useState("");
  const [finalDestination, setFinalDestination] = useState("");
  
  // State
  const [loading, setLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [legacyData, setLegacyData] = useState(null); // For basic tracking data
  const [error, setError] = useState(null);
  const [showEvents, setShowEvents] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [arrivedAtDestination, setArrivedAtDestination] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [showPhotos, setShowPhotos] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const mapRef = useRef(null);

  // Fetch container photos
  const fetchPhotos = async (containerNum) => {
    try {
      const response = await axios.get(`${API_URL}/photos/container/${containerNum}`, {
        headers: getAuthHeaders(),
      });
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error("Failed to fetch photos:", error);
      setPhotos([]);
    }
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    
    if (!trackingNumber.trim()) {
      toast.error("Please enter a tracking number");
      return;
    }
    
    setLoading(true);
    setError(null);
    setTrackingData(null);
    setLegacyData(null);
    setArrivedAtDestination(false);
    setPhotos([]);
    
    const number = trackingNumber.trim().toUpperCase();
    const emailList = notificationEmails.split(/[,;\s]+/).filter(e => e.trim());
    
    try {
      // Try SeaRates enhanced tracking first
      const searatesResponse = await axios.post(
        `${API_URL}/tracking/searates`,
        {
          number: number,
          carrier: carrier || null,
          tracking_type: trackingType,
          include_route: true,
          include_ais: true,
        },
        { headers: getAuthHeaders() }
      );
      
      if (searatesResponse.data.success) {
        setTrackingData(searatesResponse.data);
        
        // Also call the legacy tracking to save notification settings
        if (pfNumber || emailList.length > 0 || transportType === "combined_transport") {
          try {
            const legacyResponse = await axios.post(
              `${API_URL}/tracking/search`,
              { 
                container_number: number,
                carrier: carrier || null,
                destination_port: destinationPort || null,
                transport_type: transportType,
                metrans_ref: transportType === "combined_transport" ? metransRef : null,
                final_destination: transportType === "combined_transport" ? finalDestination : null,
                europacific_ref: pfNumber || null,
                notification_emails: emailList
              },
              { headers: getAuthHeaders() }
            );
            setLegacyData(legacyResponse.data);
          } catch (err) {
            console.log("Legacy tracking call failed, but SeaRates succeeded");
          }
        }
        
        await fetchPhotos(number);
        
        // Check arrival status
        const routeInfo = searatesResponse.data.route_info;
        if (routeInfo?.pod?.actual && destinationPort) {
          const podName = routeInfo.pod.location_name?.toUpperCase() || "";
          if (podName.includes(destinationPort.toUpperCase())) {
            setArrivedAtDestination(true);
            toast.success(`Container arrived at ${destinationPort}! Tracking complete.`);
          } else {
            toast.success("Container tracked successfully!");
          }
        } else {
          toast.success("Container tracked successfully!");
        }
        
        if (pfNumber || emailList.length > 0) {
          toast.info("Notifications configured for this shipment");
        }
        
      } else {
        // Fall back to legacy tracking
        const legacyResponse = await axios.post(
          `${API_URL}/tracking/search`,
          { 
            container_number: number,
            carrier: carrier || null,
            destination_port: destinationPort || null,
            transport_type: transportType,
            metrans_ref: transportType === "combined_transport" ? metransRef : null,
            final_destination: transportType === "combined_transport" ? finalDestination : null,
            europacific_ref: pfNumber || null,
            notification_emails: emailList
          },
          { headers: getAuthHeaders() }
        );
        
        setLegacyData(legacyResponse.data);
        await fetchPhotos(number);
        toast.success("Container found!");
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.message || err.message || "Failed to track";
      
      if (err.response?.status === 404) {
        const errorDetail = err.response.data.detail;
        if (typeof errorDetail === 'object') {
          setError({
            message: errorDetail.message,
            carrier: errorDetail.carrier,
            directUrl: errorDetail.direct_tracking_url
          });
        } else {
          setError({ message: errorDetail || "Container not found. Our team has been notified." });
        }
        toast.info("Container not found. We'll track it manually.");
      } else {
        setError({ message });
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Get current container status display
  const getContainerInfo = () => {
    if (!trackingData?.containers?.length) return null;
    return trackingData.containers[0];
  };

  // Get vessel info from AIS data
  const getVesselAIS = () => {
    return trackingData?.route_data?.ais?.data;
  };

  // Get route coordinates for map
  const getRouteSegments = () => {
    if (!trackingData?.route_data?.route) return [];
    return trackingData.route_data.route;
  };

  // Get current vessel position
  const getVesselPosition = () => {
    const pin = trackingData?.route_data?.pin;
    if (pin && pin.length >= 2 && isFinite(pin[0]) && isFinite(pin[1])) return pin;
    
    const aisData = getVesselAIS();
    if (aisData?.last_vessel_position) {
      const { lat, lng } = aisData.last_vessel_position;
      if (isFinite(lat) && isFinite(lng)) return [lat, lng];
    }
    return null;
  };

  // Get POL/POD locations for map markers
  const getPortLocations = () => {
    const locations = [];
    const routeInfo = trackingData?.route_info;
    const locationsMap = {};
    
    trackingData?.locations?.forEach(loc => {
      locationsMap[loc.id] = loc;
    });
    
    if (routeInfo?.pol?.location) {
      const polLoc = locationsMap[routeInfo.pol.location];
      if (polLoc?.lat && polLoc?.lng && isFinite(polLoc.lat) && isFinite(polLoc.lng)) {
        locations.push({
          type: "POL",
          name: polLoc.name,
          country: polLoc.country,
          lat: polLoc.lat,
          lng: polLoc.lng,
          date: routeInfo.pol.date,
          actual: routeInfo.pol.actual,
        });
      }
    }
    
    if (routeInfo?.pod?.location) {
      const podLoc = locationsMap[routeInfo.pod.location];
      if (podLoc?.lat && podLoc?.lng && isFinite(podLoc.lat) && isFinite(podLoc.lng)) {
        locations.push({
          type: "POD",
          name: podLoc.name,
          country: podLoc.country,
          lat: podLoc.lat,
          lng: podLoc.lng,
          date: routeInfo.pod.date,
          actual: routeInfo.pod.actual,
        });
      }
    }
    
    return locations;
  };

  // Status badge color
  const getStatusColor = (status) => {
    const s = (status || "").toUpperCase();
    if (s.includes("TRANSIT") || s.includes("LOADED")) return "bg-blue-100 text-blue-800 border-blue-200";
    if (s.includes("ARRIVED") || s.includes("DELIVERED") || s.includes("DISCHARGED")) return "bg-green-100 text-green-800 border-green-200";
    if (s.includes("PENDING") || s.includes("CUSTOMS")) return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-slate-100 text-slate-800 border-slate-200";
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const containerInfo = getContainerInfo();
  const vesselAIS = getVesselAIS();
  const routeSegments = getRouteSegments();
  const vesselPosition = getVesselPosition();
  const portLocations = getPortLocations();
  const hasMapData = routeSegments.length > 0 || vesselPosition;

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-surface p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
            <Globe className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">Container Tracking</h2>
            <p className="text-sm text-muted-foreground">Track containers worldwide with real-time map visualization</p>
          </div>
        </div>

        <form onSubmit={handleTrack} className="space-y-4">
          {/* Tracking Type Selection */}
          <div className="flex flex-wrap gap-2">
            {TRACKING_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <Button
                  key={type.value}
                  type="button"
                  variant={trackingType === type.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTrackingType(type.value)}
                  className={trackingType === type.value ? "bg-blue-600 hover:bg-blue-700" : ""}
                  data-testid={`tracking-type-${type.value}`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {type.label}
                </Button>
              );
            })}
          </div>

          {/* Main Search Row */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Container className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={
                  trackingType === "CT" ? "Enter container number (e.g., MSCU1234567)" :
                  trackingType === "BL" ? "Enter Bill of Lading number" :
                  "Enter Booking number"
                }
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                className="pl-12 h-12 text-lg font-mono"
                data-testid="tracking-input"
              />
            </div>
            <div className="lg:w-48">
              <select
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                className="w-full h-12 px-4 rounded-lg border border-border bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                data-testid="carrier-select"
              >
                {CARRIERS.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="lg:w-48">
              <select
                value={destinationPort}
                onChange={(e) => setDestinationPort(e.target.value)}
                className="w-full h-12 px-4 rounded-lg border border-border bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                data-testid="port-select"
              >
                <option value="">Destination Port...</option>
                {COMMON_PORTS.map((port) => (
                  <option key={port.code} value={port.code}>{port.label}</option>
                ))}
              </select>
            </div>
            <Button
              type="submit"
              className="h-12 px-8 bg-blue-600 hover:bg-blue-700"
              disabled={loading}
              data-testid="track-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Search className="h-5 w-5 mr-2" />
                  Track
                </>
              )}
            </Button>
          </div>

          {/* Transport Type Selection */}
          <div className="flex flex-wrap gap-4 items-center">
            <Label className="text-sm font-medium text-muted-foreground">Transport Type:</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={transportType === "port_to_port" ? "default" : "outline"}
                size="sm"
                onClick={() => setTransportType("port_to_port")}
                className={transportType === "port_to_port" ? "bg-blue-600 hover:bg-blue-700" : ""}
              >
                <Ship className="h-4 w-4 mr-2" />
                Port to Port
              </Button>
              <Button
                type="button"
                variant={transportType === "combined_transport" ? "default" : "outline"}
                size="sm"
                onClick={() => setTransportType("combined_transport")}
                className={transportType === "combined_transport" ? "bg-blue-600 hover:bg-blue-700" : ""}
              >
                <Train className="h-4 w-4 mr-2" />
                Combined (Sea + Rail)
              </Button>
            </div>
          </div>

          {/* Combined Transport Options */}
          <AnimatePresence>
            {transportType === "combined_transport" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="grid md:grid-cols-2 gap-4 pt-4 border-t border-border"
              >
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <Train className="h-4 w-4" />
                    Metrans Reference
                  </Label>
                  <Input
                    value={metransRef}
                    onChange={(e) => setMetransRef(e.target.value)}
                    placeholder="e.g., MT123456"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Final Destination
                  </Label>
                  <Input
                    value={finalDestination}
                    onChange={(e) => setFinalDestination(e.target.value)}
                    placeholder="e.g., Budapest, Hungary"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Staff Options - PF Number & Notifications */}
          {isEuropacificUser && (
            <div className="pt-4 border-t border-border">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground mb-4"
              >
                {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Staff Options (PF Link & Notifications)
              </button>
              
              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid md:grid-cols-2 gap-4"
                  >
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                        <FolderOpen className="h-4 w-4" />
                        Position Folder (PF) Number
                      </Label>
                      <Input
                        value={pfNumber}
                        onChange={(e) => setPfNumber(e.target.value)}
                        placeholder="e.g., PF-2026-001234"
                        data-testid="pf-number-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Link this shipment to a Position Folder</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Notification Emails
                      </Label>
                      <Input
                        value={notificationEmails}
                        onChange={(e) => setNotificationEmails(e.target.value)}
                        placeholder="email1@company.com, email2@company.com"
                        data-testid="notification-emails-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Comma-separated emails for status updates</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </form>
      </motion.div>

      {/* Error Display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="card-surface p-6 border-l-4 border-amber-500"
          >
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-amber-500 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">Container Not Found</h3>
                <p className="text-muted-foreground mb-4">{error.message}</p>
                {error.directUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(error.directUrl, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Try Direct Tracking ({error.carrier})
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Arrival Banner */}
      <AnimatePresence>
        {arrivedAtDestination && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-4"
          >
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <h3 className="font-bold text-green-800">Container Arrived!</h3>
              <p className="text-green-700">Your container has arrived at {destinationPort}. Tracking complete.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tracking Results */}
      <AnimatePresence>
        {(trackingData || legacyData) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
            data-testid="tracking-result"
          >
            {/* Map Visualization */}
            {hasMapData && (
              <div className="card-surface overflow-hidden">
                <div className="h-[400px] relative">
                  <MapContainer
                    ref={mapRef}
                    center={vesselPosition && isFinite(vesselPosition[0]) && isFinite(vesselPosition[1]) ? vesselPosition : [20, 0]}
                    zoom={2}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {/* Route lines */}
                    {routeSegments.map((segment, idx) => {
                      if (!segment.path) return null;
                      const positions = segment.path.filter(p => Array.isArray(p) && p.length >= 2 && isFinite(p[0]) && isFinite(p[1]));
                      if (positions.length < 2) return null;
                      
                      return (
                        <Polyline
                          key={idx}
                          positions={positions}
                          color={segment.type === "SEA" ? "#2563eb" : "#059669"}
                          weight={3}
                          opacity={0.8}
                          dashArray={segment.type === "LAND" ? "10, 10" : null}
                        />
                      );
                    })}
                    
                    {/* Port markers */}
                    {portLocations.map((port, idx) => (
                      <Marker
                        key={`port-${idx}`}
                        position={[port.lat, port.lng]}
                        icon={portIcon}
                      >
                        <Popup>
                          <div className="text-sm">
                            <p className="font-bold">{port.type}: {port.name}</p>
                            <p className="text-muted-foreground">{port.country}</p>
                            {port.date && (
                              <p className="text-xs mt-1">
                                {port.actual ? "Actual" : "Estimated"}: {formatDate(port.date)}
                              </p>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                    
                    {/* Vessel position marker */}
                    {vesselPosition && (
                      <Marker position={vesselPosition} icon={vesselIcon}>
                        <Popup>
                          <div className="text-sm">
                            <p className="font-bold flex items-center gap-1">
                              <Ship className="h-4 w-4" />
                              {vesselAIS?.vessel?.name || "Vessel Position"}
                            </p>
                            {vesselAIS?.vessel?.imo && (
                              <p className="text-xs text-muted-foreground">IMO: {vesselAIS.vessel.imo}</p>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    )}
                    
                    <MapBoundsFitter route={routeSegments} vesselPosition={vesselPosition} />
                  </MapContainer>
                  
                  {/* Map Legend */}
                  <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-lg p-3 shadow-lg z-[1000]">
                    <div className="text-xs font-medium mb-2">Legend</div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-0.5 bg-blue-600"></div>
                        <span>Sea Route</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-0.5 bg-emerald-600" style={{ borderBottom: "2px dashed" }}></div>
                        <span>Land Route</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                        <span>Vessel</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-emerald-600 rounded-full"></div>
                        <span>Port</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Container & Status Summary */}
            <div className="card-surface p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Container className="h-7 w-7 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-mono text-2xl font-bold text-foreground">
                      {containerInfo?.number || legacyData?.container_number || trackingNumber}
                    </p>
                    <p className="text-muted-foreground">
                      {trackingData?.metadata?.sealine_name || legacyData?.carrier || "Unknown Carrier"}
                      {(containerInfo?.size_type || legacyData?.container_type) && ` • ${containerInfo?.size_type || legacyData?.container_type}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {pfNumber && (
                    <span className="px-3 py-1.5 rounded-full border text-sm font-medium bg-purple-100 text-purple-800 border-purple-200 flex items-center gap-2">
                      <FolderOpen className="h-3 w-3" />
                      {pfNumber}
                    </span>
                  )}
                  <span className={`px-4 py-2 rounded-full border text-sm font-medium ${getStatusColor(containerInfo?.status || legacyData?.status || trackingData?.metadata?.status)}`}>
                    <span className="flex items-center gap-2">
                      <Radio className="h-3 w-3" />
                      {containerInfo?.status || legacyData?.status || trackingData?.metadata?.status || "Unknown"}
                    </span>
                  </span>
                </div>
              </div>

              {/* Route Summary */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* POL */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-600 mb-2">
                    <Anchor className="h-4 w-4" />
                    <span className="text-sm font-medium">Port of Loading</span>
                  </div>
                  <p className="font-semibold text-foreground">
                    {trackingData?.route_info?.pol?.location_name || legacyData?.port_of_loading || "N/A"}
                  </p>
                  {(trackingData?.route_info?.pol?.date || legacyData?.departure_date) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {trackingData?.route_info?.pol?.actual ? "ATD" : "ETD"}: {formatDate(trackingData?.route_info?.pol?.date || legacyData?.departure_date)}
                    </p>
                  )}
                </div>

                {/* POD */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-600 mb-2">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm font-medium">Port of Discharge</span>
                  </div>
                  <p className="font-semibold text-foreground">
                    {trackingData?.route_info?.pod?.location_name || legacyData?.port_of_discharge || "N/A"}
                  </p>
                  {(trackingData?.route_info?.pod?.date || legacyData?.eta) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {trackingData?.route_info?.pod?.actual ? "ATA" : "ETA"}: {formatDate(trackingData?.route_info?.pod?.date || legacyData?.eta)}
                    </p>
                  )}
                </div>

                {/* Vessel */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-indigo-600 mb-2">
                    <Ship className="h-4 w-4" />
                    <span className="text-sm font-medium">Current Vessel</span>
                  </div>
                  <p className="font-semibold text-foreground">
                    {vesselAIS?.vessel?.name || trackingData?.vessels?.[0]?.name || legacyData?.vessel_name || "N/A"}
                  </p>
                  {(vesselAIS?.vessel?.imo || legacyData?.voyage) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {vesselAIS?.vessel?.imo ? `IMO: ${vesselAIS.vessel.imo}` : `Voyage: ${legacyData?.voyage}`}
                    </p>
                  )}
                </div>

                {/* Last Update */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-600 mb-2">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm font-medium">Last Update</span>
                  </div>
                  <p className="font-semibold text-foreground">
                    {formatDate(trackingData?.metadata?.updated_at || legacyData?.last_update)}
                  </p>
                  {trackingData?.metadata?.from_cache && (
                    <p className="text-xs text-muted-foreground mt-1">Cached data</p>
                  )}
                </div>
              </div>

              {/* Actions Row */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
                {photos.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => setShowPhotos(!showPhotos)}>
                    <Camera className="h-4 w-4 mr-2" />
                    Photos ({photos.length})
                  </Button>
                )}
                {trackingNumber && (
                  <ReminderButton
                    referenceType="container"
                    referenceNumber={trackingNumber}
                    getAuthHeaders={getAuthHeaders}
                  />
                )}
                {isEuropacificUser && (
                  <Button variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add to Watchlist
                  </Button>
                )}
              </div>
            </div>

            {/* Photos Section */}
            <AnimatePresence>
              {showPhotos && photos.length > 0 && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="card-surface p-6"
                >
                  <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
                    <Image className="h-5 w-5" />
                    Container Photos
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {photos.map((photo, idx) => (
                      <div
                        key={idx}
                        className="aspect-video bg-slate-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedPhoto(photo)}
                      >
                        <img src={photo.url} alt={photo.description || `Photo ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Vessel AIS Details */}
            {vesselAIS && (
              <div className="card-surface p-6">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="w-full flex items-center justify-between text-left mb-4"
                >
                  <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
                    <Navigation className="h-5 w-5 text-blue-600" />
                    Vessel AIS Information
                  </h3>
                  {showDetails ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>

                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="grid md:grid-cols-3 gap-4"
                    >
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                          <Ship className="h-4 w-4" />
                          Vessel Details
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-muted-foreground">Name:</span> <strong>{vesselAIS.vessel?.name}</strong></p>
                          <p><span className="text-muted-foreground">IMO:</span> {vesselAIS.vessel?.imo}</p>
                          <p><span className="text-muted-foreground">MMSI:</span> {vesselAIS.vessel?.mmsi}</p>
                          <p><span className="text-muted-foreground">Call Sign:</span> {vesselAIS.vessel?.call_sign}</p>
                          <p><span className="text-muted-foreground">Flag:</span> {vesselAIS.vessel?.flag}</p>
                        </div>
                      </div>

                      <div className="bg-emerald-50 rounded-lg p-4">
                        <h4 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
                          <ArrowRight className="h-4 w-4" />
                          Departure Port
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-muted-foreground">Port:</span> <strong>{vesselAIS.departure_port?.code}</strong></p>
                          <p><span className="text-muted-foreground">Country:</span> {vesselAIS.departure_port?.country_code}</p>
                          <p>
                            <span className="text-muted-foreground">{vesselAIS.departure_port?.date_label}:</span>{" "}
                            {formatDate(vesselAIS.departure_port?.date)}
                          </p>
                        </div>
                      </div>

                      <div className="bg-indigo-50 rounded-lg p-4">
                        <h4 className="font-medium text-indigo-800 mb-3 flex items-center gap-2">
                          <LocateFixed className="h-4 w-4" />
                          Next Port
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-muted-foreground">Port:</span> <strong>{vesselAIS.arrival_port?.code}</strong></p>
                          <p><span className="text-muted-foreground">Country:</span> {vesselAIS.arrival_port?.country_code}</p>
                          <p>
                            <span className="text-muted-foreground">{vesselAIS.arrival_port?.date_label}:</span>{" "}
                            {formatDate(vesselAIS.arrival_port?.date)}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Events Timeline */}
            {containerInfo?.events?.length > 0 && (
              <div className="card-surface p-6">
                <button
                  onClick={() => setShowEvents(!showEvents)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
                    <Clock className="h-5 w-5 text-blue-600" />
                    Events Timeline ({containerInfo.events.length})
                  </h3>
                  {showEvents ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>

                <AnimatePresence>
                  {showEvents && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-6 space-y-4"
                    >
                      {[...containerInfo.events].reverse().map((event, index) => {
                        const isFirst = index === 0;
                        const isSea = event.transport_type === "VESSEL";
                        const isLand = event.transport_type === "TRUCK" || event.type === "land";
                        
                        return (
                          <div
                            key={index}
                            className={`flex gap-4 pb-4 ${index < containerInfo.events.length - 1 ? "border-b border-border" : ""}`}
                          >
                            <div className="relative">
                              <div
                                className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                  isFirst
                                    ? "bg-blue-600"
                                    : event.actual
                                    ? "bg-emerald-100 border-2 border-emerald-500"
                                    : "bg-slate-100 border-2 border-slate-300"
                                }`}
                              >
                                {isSea ? (
                                  <Waves className={`h-4 w-4 ${isFirst ? "text-white" : "text-blue-600"}`} />
                                ) : isLand ? (
                                  <Truck className={`h-4 w-4 ${isFirst ? "text-white" : "text-emerald-600"}`} />
                                ) : (
                                  <div className={`h-2 w-2 rounded-full ${isFirst ? "bg-white" : "bg-slate-400"}`} />
                                )}
                              </div>
                              {index < containerInfo.events.length - 1 && (
                                <div className="absolute top-8 left-1/2 -translate-x-1/2 w-0.5 h-full bg-slate-200" />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                  <p className="font-medium text-foreground">{event.description}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {event.location?.name && `${event.location.name}`}
                                    {event.location?.country && `, ${event.location.country}`}
                                  </p>
                                  {event.facility?.name && (
                                    <p className="text-xs text-muted-foreground mt-0.5">{event.facility.name}</p>
                                  )}
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-mono text-foreground">{formatDate(event.date)}</p>
                                  <p className={`text-xs ${event.actual ? "text-emerald-600" : "text-amber-600"}`}>
                                    {event.actual ? "Actual" : "Estimated"}
                                  </p>
                                </div>
                              </div>
                              
                              {event.vessel?.name && (
                                <div className="mt-2 inline-flex items-center gap-2 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                  <Ship className="h-3 w-3" />
                                  {event.vessel.name}
                                  {event.voyage && <span className="text-blue-500">• Voyage: {event.voyage}</span>}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Legacy Events (if no SeaRates events) */}
            {!containerInfo?.events?.length && legacyData?.events?.length > 0 && (
              <div className="card-surface p-6">
                <button
                  onClick={() => setShowEvents(!showEvents)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
                    <Clock className="h-5 w-5 text-blue-600" />
                    Events Timeline ({legacyData.events.length})
                  </h3>
                  {showEvents ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>

                <AnimatePresence>
                  {showEvents && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-6 space-y-3"
                    >
                      {legacyData.events.map((event, index) => (
                        <div key={index} className="flex gap-4 items-start pb-3 border-b border-border last:border-0">
                          <div className={`h-6 w-6 rounded-full flex items-center justify-center ${index === 0 ? "bg-blue-600" : "bg-slate-200"}`}>
                            <div className={`h-2 w-2 rounded-full ${index === 0 ? "bg-white" : "bg-slate-400"}`} />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-foreground">{event.description || event.event}</p>
                            <p className="text-sm text-muted-foreground">{event.location}</p>
                          </div>
                          <p className="text-sm font-mono text-muted-foreground">{formatDate(event.date || event.timestamp)}</p>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Note when no data */}
      {!trackingData && !legacyData && !loading && !error && (
        <div className="card-surface p-6 border-l-4 border-blue-500">
          <div className="flex items-start gap-4">
            <Info className="h-6 w-6 text-blue-500 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-foreground">Container Tracking</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Track containers by number, Bill of Lading, or Booking number.
                Results include real-time vessel position, route visualization, and detailed event timeline.
              </p>
              {isEuropacificUser && (
                <p className="text-muted-foreground text-sm mt-2">
                  <strong>Staff:</strong> Link shipments to Position Folders and configure customer email notifications.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="max-w-4xl max-h-[90vh] relative">
            <img src={selectedPhoto.url} alt={selectedPhoto.description} className="max-w-full max-h-[90vh] object-contain rounded-lg" />
            <button
              className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70"
              onClick={() => setSelectedPhoto(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
